import { API_PATHS, apiHeaders, apiUrl } from '../config/api';
import { Course, CoursesResponse } from '../types';

export const FLAGSHIP_SUBCATEGORY = 'Flagship';

export function isTaggedFlagship(course: { subcategory?: string | null }): boolean {
  return (course.subcategory ?? '').trim().toLowerCase() === FLAGSHIP_SUBCATEGORY.toLowerCase();
}

/** True when at least one active course exists (Programs nav is useful). */
export function showProgramsMenu(courses: unknown[]): boolean {
  return courses.length >= 1;
}

/**
 * A course is Flagship when tagged in subcategory, or when it is the only
 * active course in the catalog.
 */
export function isFlagshipCourse(
  course: { subcategory?: string | null },
  allActiveCourses?: unknown[]
): boolean {
  if (isTaggedFlagship(course)) {
    return true;
  }
  return Array.isArray(allActiveCourses) && allActiveCourses.length === 1;
}

export function findFlagshipCourse<T extends { subcategory?: string | null }>(courses: T[]): T | undefined {
  return courses.find(isTaggedFlagship) ?? (courses.length === 1 ? courses[0] : undefined);
}

export function isEnrollmentOpen(course: { lastDateForEnrollment?: string | null }): boolean {
  const raw = course.lastDateForEnrollment;
  if (!raw) return true;
  const last = new Date(raw);
  if (Number.isNaN(last.getTime())) return true;
  const endOfLastDay = new Date(last.getFullYear(), last.getMonth(), last.getDate(), 23, 59, 59, 999);
  return endOfLastDay.getTime() >= Date.now();
}

export function enrollableCourses<T extends { lastDateForEnrollment?: string | null }>(courses: T[]): T[] {
  return courses.filter(isEnrollmentOpen);
}

/** Public URL for an IDC Academy program (Academy /api/programs). Not content /courses. */
export function programDetailPath(courseId: number): string {
  return `/programs/${courseId}`;
}

export function overviewExcerpt(overview: string | null | undefined, max = 220): string {
  const plain = (overview || '').replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();
  if (plain.length <= max) return plain;
  return `${plain.slice(0, max).trim()}…`;
}

let cachedActiveCourses: Course[] | null = null;
let activeCoursesInflight: Promise<Course[]> | null = null;

export async function fetchActiveCourses(): Promise<Course[]> {
  if (cachedActiveCourses) {
    return cachedActiveCourses;
  }
  if (!activeCoursesInflight) {
    activeCoursesInflight = (async () => {
      const response = await fetch(`${apiUrl(API_PATHS.courses.list)}?scope=light&pageSize=100&enrollmentAllowedOnly=true`, {
        headers: apiHeaders(),
      });
      const data: CoursesResponse = await response.json();
      cachedActiveCourses = data.success ? enrollableCourses(data.courses || []) : [];
      return cachedActiveCourses;
    })().finally(() => {
      activeCoursesInflight = null;
    });
  }
  return activeCoursesInflight;
}

const inactiveEnrollmentStatuses = new Set(['cancelled', 'rejected']);

export function isActiveApplication(enrollment: {
  status?: string | null;
  isActive?: boolean | null;
}): boolean {
  if (enrollment.isActive === false) return false;
  const status = (enrollment.status || '').trim().toLowerCase();
  return !inactiveEnrollmentStatuses.has(status);
}

/** Course ids the signed-in user has already applied to (excludes cancelled/rejected). */
export async function fetchAppliedCourseIds(
  userProfileId: number,
  token?: string | null
): Promise<Set<number>> {
  const response = await fetch(apiUrl(API_PATHS.enrollments.byUser(userProfileId)), {
    headers: apiHeaders(token || localStorage.getItem('token')),
  });
  const data = await response.json();
  const ids = ((data.enrolledCourses || []) as Array<{ courseId: number; status?: string; isActive?: boolean | null }>)
    .filter(isActiveApplication)
    .map((enrollment) => enrollment.courseId);
  return new Set(ids);
}
